import { type OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMemo } from "react";

import { useGetWorkerShifts } from "../../WorkerShifts/api/useGetWorkerShifts";
import { type OpenShiftWorkplace } from "../Open/types";
import {
  hasPreShiftOrientation,
  type PreShiftOrientationEvaluation,
} from "./hasPreShiftOrientation";
import { useWorkplacesWithOrientationCredentials } from "./useWorkplacesWithOrientationCredentials";
import { useWorkplacesWithUpcomingPreShiftOrientation } from "./useWorkplacesWithUpcomingPreShiftOrientation";

/**
 * Evaluates pre-shift orientation for all shifts in a list, returning a map
 * keyed by shift ID.
 */
export function usePreShiftOrientationsByShiftId(
  shifts: OpenShift[],
  workplacesMap: Map<string, OpenShiftWorkplace>
): Map<string, PreShiftOrientationEvaluation> {
  const worker = useDefinedWorker();

  const { workplacesWithOrientationCredentials, isLoading: isLoadingCredentials } =
    useWorkplacesWithOrientationCredentials();
  const { workplacesWithUpcomingPreShiftOrientation, isLoading: isLoadingActiveOrientations } =
    useWorkplacesWithUpcomingPreShiftOrientation();
  const { shifts: workerShifts, isLoading: isLoadingBookedShifts } = useGetWorkerShifts({ worker });

  const bookedShifts = useMemo(
    () =>
      workerShifts.map((workerShift) => ({
        start: workerShift.start,
        workplaceId: workerShift.facility.userId ?? "",
      })),
    [workerShifts]
  );

  const isLoading = isLoadingCredentials || isLoadingActiveOrientations || isLoadingBookedShifts;

  return useMemo(() => {
    if (isLoading) {
      return new Map<string, PreShiftOrientationEvaluation>();
    }

    const map = new Map<string, PreShiftOrientationEvaluation>();

    for (const shift of shifts) {
      const workplaceId = shift.relationships.workplace.data.id;
      const workplace = workplacesMap.get(workplaceId);

      const evaluation = hasPreShiftOrientation({
        workplaceId,
        shiftStart: shift.attributes.start,
        preShiftOrientationLengthInMinutes:
          workplace?.attributes.preShiftOrientationLengthInMinutes,
        workplacesWithOrientationCredentials,
        workplacesWithUpcomingPreShiftOrientation,
        bookedShifts,
      });

      map.set(shift.id, evaluation);
    }

    return map;
  }, [
    isLoading,
    shifts,
    workplacesMap,
    workplacesWithOrientationCredentials,
    workplacesWithUpcomingPreShiftOrientation,
    bookedShifts,
  ]);
}
